import {useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {useIVStore} from '@chekin/shared';
import {PATHS} from 'routes/constants';
import {LeaderGuestFlag} from 'utils/guestForm';
import {isIVRequiredForGuest} from 'utils/identityVerification';
import {useSchema} from 'context/guest';
import {useReservation} from 'context/reservation';
import {useIVOnProperty} from 'hooks/useIVOnProperty';
import {discardIVScan} from '@/hooks/iv/ivLockStore';
import {usePath} from './common';
import {useGuestGroup} from './useGuestGroup';

function useRegisterGuestNavigation() {
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const {guestGroup} = useGuestGroup();
  const {isBiometricMatchForAll} = useReservation();
  const {resetAllIVState, setDeclaredIvBypassAge} = useIVStore();
  const {isIVOnPropertyUnavailable, isIVOnPropertyAvailable} = useIVOnProperty();
  const {
    identityVerificationDetails: {enabled: ivEnabled, underageAllowedAge},
  } = useSchema();

  const hasNoGuests = guestGroup?.number_of_fully_registered_guests === 0;

  const getNextPage = useCallback(
    (isLeader?: boolean) => {
      if (isIVOnPropertyUnavailable) return getFullPath(PATHS.addPersonalDataForm);

      const goToIv = isIVRequiredForGuest({
        isIVEnabled: ivEnabled,
        isBiometricMatchForAll,
        isLeaderGuest: isLeader ?? hasNoGuests,
      });

      if (!goToIv) {
        return getFullPath(PATHS.addPersonalDataForm);
      }

      return isIVOnPropertyAvailable
        ? getFullPath(`${PATHS.verification.main}/${PATHS.verification.qrScan}`)
        : getFullPath(PATHS.verification.main);
    },
    [
      ivEnabled,
      isIVOnPropertyUnavailable,
      isIVOnPropertyAvailable,
      getFullPath,
      isBiometricMatchForAll,
      hasNoGuests,
    ],
  );

  const handleRegisterNewGuest = useCallback(
    (isLeader?: boolean) => {
      LeaderGuestFlag.persist(isLeader);
      resetAllIVState();
      discardIVScan();
      navigate(LeaderGuestFlag.appendToPath(getNextPage(isLeader), isLeader));
    },
    [getNextPage, navigate, resetAllIVState],
  );

  const handleRegisterMinor = useCallback(() => {
    resetAllIVState();
    discardIVScan();
    setDeclaredIvBypassAge(underageAllowedAge);
    LeaderGuestFlag.persist(false);
    navigate(LeaderGuestFlag.appendToPath(getFullPath(PATHS.addPersonalDataForm), false));
  }, [
    getFullPath,
    navigate,
    resetAllIVState,
    setDeclaredIvBypassAge,
    underageAllowedAge,
  ]);

  return {getNextPage, handleRegisterNewGuest, handleRegisterMinor};
}

export {useRegisterGuestNavigation};
